/* Sura — slide-out cart (<sura-cart-drawer>) */
if (!customElements.get('sura-cart-drawer')) {
  class SuraCartDrawer extends HTMLElement {
    constructor() {
      super();
      // Bound once. addEventListener de-duplicates identical (type, fn) pairs,
      // so re-connecting the element can never stack up handlers.
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onCartEvent = this.onCartEvent.bind(this);
    }

    connectedCallback() {
      this.cacheNodes();

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      ['sura:cart:added', 'cart:refresh'].forEach((name) => {
        document.addEventListener(name, this.onCartEvent);
      });

      window.SuraCart = {
        open: () => this.open(),
        close: () => this.close(),
        refresh: () => this.refresh(),
      };
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      ['sura:cart:added', 'cart:refresh'].forEach((name) => {
        document.removeEventListener(name, this.onCartEvent);
      });
    }

    cacheNodes() {
      this.panel = this.querySelector('[data-cart-panel]');
      this.overlay = this.querySelector('[data-cart-overlay]');
      this.body = this.querySelector('[data-cart-body]');
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    /* ---------- Opening and closing ---------- */
    onDocumentClick(e) {
      if (e.target.closest('[data-cart-open]')) {
        e.preventDefault();
        this.open();
        return;
      }

      if (this.dataset.interceptCartLinks !== 'true') return;

      const link = e.target.closest('a[href]');
      if (!link || this.contains(link)) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || link.target === '_blank') return;

      const cartPath = new URL(this.dataset.cartUrl, window.location.origin).pathname;
      const url = new URL(link.href, window.location.origin);
      if (url.pathname !== cartPath || url.search) return;

      e.preventDefault();
      this.open();
    }

    onKeydown(e) {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trapFocus(e);
    }

    onCartEvent() {
      this.refresh().then(() => {
        if (this.dataset.openOnAdd === 'true') this.open();
      });
    }

    open() {
      this.lastFocused = document.activeElement;
      this.overlay.hidden = false;
      this.panel.hidden = false;
      // next frame, so the transition runs from the off-screen position
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        (this.querySelector('[data-cart-close]') || this.panel).focus();
      });
      document.body.style.overflow = 'hidden';
      this.dispatchEvent(new CustomEvent('sura:cart:open', { bubbles: true }));
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      const done = () => {
        this.panel.hidden = true;
        this.overlay.hidden = true;
      };
      this.panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 350); // in case the transition never fires
      this.lastFocused?.focus?.();
    }

    trapFocus(e) {
      const focusables = this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- Clicks inside the drawer ---------- */
    onClick(e) {
      const t = e.target;
      if (t.closest('[data-cart-close]') || t.closest('[data-cart-overlay]')) { this.close(); return; }

      const toggle = t.closest('[data-cart-toggle]');
      if (toggle) {
        const panel = this.querySelector(`[data-cart-panel-${toggle.dataset.cartToggle}]`);
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        if (panel) panel.hidden = open;
        return;
      }

      const minus = t.closest('[data-cart-minus]');
      const plus = t.closest('[data-cart-plus]');
      const remove = t.closest('[data-cart-remove]');

      if (minus || plus || remove) {
        e.preventDefault();
        const item = t.closest('[data-cart-item]');
        if (!item) return;
        if (remove) { this.setQuantity(item, 0); return; }
        const input = item.querySelector('[data-cart-qty]');
        const current = parseInt(input?.value, 10) || 0;
        this.setQuantity(item, current + (plus ? 1 : -1));
        return;
      }

      if (t.closest('[data-cart-save-note]')) { this.saveNote(); return; }
      if (t.closest('[data-cart-apply-code]')) { this.applyCode(); return; }

      const removeCode = t.closest('[data-cart-remove-code]');
      if (removeCode) this.removeCode(removeCode.dataset.cartRemoveCode);
    }

    onChange(e) {
      if (!e.target.matches('[data-cart-qty]')) return;
      this.setQuantity(e.target.closest('[data-cart-item]'), e.target.value);
    }

    /* ---------- Cart updates ---------- */
    /*
      Every write asks Shopify to return this section's HTML in the same
      response (`sections` / `sections_url`), so a failed second round-trip can
      never leave the drawer showing stale contents.
    */
    async post(url, payload) {
      this.classList.add('is-busy');
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            ...payload,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.description || data.message || `Cart update failed (${res.status}).`);
        }
        return data;
      } finally {
        this.classList.remove('is-busy');
      }
    }

    setQuantity(item, quantity) {
      if (!item || !item.dataset.key) return Promise.resolve();
      const qty = Math.max(0, parseInt(quantity, 10) || 0);
      item.classList.add('is-updating');
      this.showMessage('');
      return this.post(this.dataset.changeUrl, { id: item.dataset.key, quantity: qty })
        .then((data) => this.renderFrom(data))
        .catch((err) => {
          item.classList.remove('is-updating');
          this.showMessage(err.message);
          console.error('[sura-cart] change failed', err);
        });
    }

    saveNote() {
      const note = this.querySelector('[data-cart-note]');
      if (!note) return;
      this.post(this.dataset.updateUrl, { note: note.value })
        .then((data) => this.renderFrom(data, { keep: 'note' }))
        .catch((err) => this.showMessage(err.message));
    }

    applyCode() {
      const input = this.querySelector('[data-cart-code]');
      const code = input?.value.trim();
      if (!code) return;
      const existing = Array.from(this.querySelectorAll('[data-cart-remove-code]')).map((b) => b.dataset.cartRemoveCode);
      this.post(this.dataset.updateUrl, { discount: existing.concat(code).join(',') })
        .then((data) => this.renderFrom(data, { keep: 'discount' }))
        .then(() => {
          // Shopify silently ignores codes that don't apply, so check the result
          if (!this.querySelector(`[data-cart-remove-code="${CSS.escape(code)}"]`)) {
            this.showMessage('That code could not be applied to this cart.', 'code');
          }
        })
        .catch((err) => this.showMessage(err.message, 'code'));
    }

    removeCode(code) {
      const remaining = Array.from(this.querySelectorAll('[data-cart-remove-code]'))
        .map((b) => b.dataset.cartRemoveCode)
        .filter((c) => c !== code);
      this.post(this.dataset.updateUrl, { discount: remaining.join(',') })
        .then((data) => this.renderFrom(data, { keep: 'discount' }))
        .catch((err) => this.showMessage(err.message, 'code'));
    }

    showMessage(text, where) {
      const target =
        (where === 'code' && this.querySelector('[data-cart-code-msg]')) ||
        this.querySelector('[data-cart-error]') ||
        this.querySelector('[data-cart-code-msg]');
      if (target) target.textContent = text;
      else if (text) console.error('[sura-cart]', text);
    }

    /* ---------- Re-render from Shopify ---------- */
    renderFrom(data, options = {}) {
      const html = data?.sections?.[this.sectionId];
      if (html) return this.replaceBody(html, options);
      // Older API shape, or a store that didn't honour `sections` — fetch it.
      return this.refresh(options);
    }

    async refresh(options = {}) {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(this.sectionId)}`;
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        const html = data[this.sectionId];
        if (!html) throw new Error(`Section "${this.sectionId}" was not returned by ${url}`);
        this.replaceBody(html, options);
      } catch (err) {
        console.error('[sura-cart] refresh failed', err);
        this.showMessage('Could not refresh the cart. Please reload the page.');
      }
    }

    replaceBody(html, options = {}) {
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const freshBody = fresh.querySelector('[data-cart-body]');
      if (!freshBody || !this.body) return;

      this.body.replaceWith(freshBody);
      this.body = this.querySelector('[data-cart-body]');

      const count = fresh.querySelector('sura-cart-drawer')?.dataset.itemCount;
      if (count !== undefined) {
        this.dataset.itemCount = count;
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = count;
          el.classList.toggle('is-empty', count === '0');
        });
      }

      // Keep the note or discount panel open after saving from it
      if (options.keep) {
        const toggle = this.querySelector(`[data-cart-toggle="${options.keep}"]`);
        const panel = this.querySelector(`[data-cart-panel-${options.keep}]`);
        if (toggle && panel) { toggle.setAttribute('aria-expanded', 'true'); panel.hidden = false; }
      }

      this.dispatchEvent(new CustomEvent('sura:cart:updated', { bubbles: true, detail: { count } }));
    }
  }

  customElements.define('sura-cart-drawer', SuraCartDrawer);
}